import moment from 'moment-timezone';

import * as net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

export enum AuditLogFormat {
  CSV = 'CSV',
  PDF = 'PDF',
}

export interface ExportAuditLogsResponse {
  url: string;
}

export interface ExportAuditLogsDeferredResponse {
  url?: string; // Optional URL if ready immediately
  message?: string; // Optional message about email notification
}

export interface ExportHistoryReportResponse {
  succeeded: boolean;
  url: string;
  message: string;
}

export enum DisallowedMoveReason {
  ENABLED_TO_DISABLED = 'enabled project to disabled project',
  DISABLED_TO_ENABLED = 'disabled project to enabled project',
}

export interface CheckFolderMoveAllowedByDataModsResponse {
  move_allowed: boolean;
  data_mods_reason_required: boolean;
  disallowed_api_ids: {[apiId: string]: DisallowedMoveReason};
}

export interface AuditLogRowData {
  [key: string]: string;
}

export interface AuditLogPagination {
  total: number;
  pageSize: number;
  pageOffset: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface AuditLogObjectMetadata {
  title: string;
}

export interface AuditLogsDataResponse {
  columns: Array<string>;
  data: Array<AuditLogRowData>;
  pagination: AuditLogPagination;
  objectMetadata: AuditLogObjectMetadata;
}

export interface AuditFilterUser {
  id: string;
  name: string;
  handle: string;
}

export interface ActionFilterCriteria {
  effect_type: string;
  changed_property: string | null;
  affected_object_type: string | null;
  action: string | null;
}

export interface AuditFilterAction {
  display_name: string;
  filters: Array<ActionFilterCriteria>;
}

export interface AuditFilterOptionsResponse {
  users: Array<AuditFilterUser>;
  actions: Array<AuditFilterAction>;
  reasonCodes: Array<string> | null;
  displayTimezone: string | null;
}

export function exportAuditLogs(
  ids: Array<string>,
  format: AuditLogFormat,
  additionalOptions: {resultsWarehouseIds?: Array<string>} = {}
): JQueryPromise<ExportAuditLogsDeferredResponse> {
  return TaskRunner.postJSON({
    data: {ids, format, ...additionalOptions, browser_timezone: moment.tz.guess()},
    url: '/1/api/audits:export-logs',
  });
}

export function exportRelatedResultsAuditLogs(
  id: string,
  format: AuditLogFormat
): JQueryPromise<ExportAuditLogsDeferredResponse> {
  return TaskRunner.postJSON({
    data: {apiId: id, exportFormat: format},
    url: '/1/api/audits:export-related-results-logs',
  });
}

export function exportSampleContainerHistory(
  containerApiIds: Array<string>,
  exportFormat: AuditLogFormat
): JQueryPromise<ExportHistoryReportResponse> {
  return TaskRunner.postJSON({
    data: {
      container_api_ids: containerApiIds,
      export_format: exportFormat,
      browser_timezone: moment.tz.guess(),
    },
    url: '/1/api/audits:export-sample-container-history-report',
  });
}

export function exportPlateHistory(
  plateApiIds: Array<string>,
  exportFormat: AuditLogFormat
): JQueryPromise<ExportHistoryReportResponse> {
  return TaskRunner.postJSON({
    data: {
      plate_api_ids: plateApiIds,
      export_format: exportFormat,
      browser_timezone: moment.tz.guess(),
    },
    url: '/1/api/audits:export-plate-history-report',
  });
}

export function exportDataModsTACReport(format: AuditLogFormat): JQueryPromise<ExportAuditLogsResponse> {
  return TaskRunner.postJSON({
    data: {export_format: format, browser_timezone: moment.tz.guess()},
    url: '/1/api/audits:export-data-mods-settings-logs',
  });
}

export function exportLocationHistoryReport(
  storableApiIds: Array<string>,
  exportFormat: AuditLogFormat,
  startTime?: string,
  endTime?: string
): Promise<TaskRunner.OngoingTaskResponse> {
  return net.postJSONPromise({
    data: {
      storable_api_ids: storableApiIds,
      export_format: exportFormat,
      start_time: startTime,
      end_time: endTime,
      browser_timezone: moment.tz.guess(),
    },
    url: '/1/api/audits:export-location-history-report',
  });
}

export function exportIPAllowlistsTACReport(
  format: AuditLogFormat
): JQueryPromise<ExportAuditLogsDeferredResponse> {
  return TaskRunner.postJSON({
    data: {export_format: format, browser_timezone: moment.tz.guess()},
    url: '/1/api/tenant:export-ip-allowlists-audit-logs',
  });
}

export function exportAISettingsTACReport(format: AuditLogFormat): JQueryPromise<ExportAuditLogsResponse> {
  return TaskRunner.postJSON({
    data: {export_format: format, browser_timezone: moment.tz.guess()},
    url: '/1/api/tenant:export-ai-settings-audit-logs',
  });
}

export function checkDataModsReasonRequired(
  apiIds?: Array<string>
): Promise<{data_mods_reason_required: boolean}> {
  return net.postJSONPromise({
    data: {api_ids: apiIds},
    url: '/1/api/audits:check-data-mods-reason-required',
  });
}

export function checkFolderMoveAllowedByDataMods(
  apiIds: Array<string | null>,
  destinationFolderApiId: string | null
): Promise<CheckFolderMoveAllowedByDataModsResponse> {
  apiIds = apiIds.filter((apiId) => typeof apiId === 'string');
  destinationFolderApiId = destinationFolderApiId ?? '';
  return net.postJSONPromise({
    data: {api_ids: apiIds, destination_folder_api_id: destinationFolderApiId},
    url: '/1/api/audits:check-folder-move-allowed-by-data-mods',
  });
}

export function exportTenantCapabilitiesReport(
  format: AuditLogFormat
): JQueryPromise<ExportAuditLogsResponse> {
  return TaskRunner.postJSON({
    data: {export_format: format, browser_timezone: moment.tz.guess()},
    url: '/1/api/audits:export-tenant-capabilities-audit-logs',
  });
}

export function getFilterOptions(
  apiIds: Array<string>,
  options: {
    resultsWarehouseIds?: Array<string>;
    relatedResultsModelId?: string;
  } = {}
): Promise<AuditFilterOptionsResponse> {
  const params = new URLSearchParams();
  if (apiIds.length > 0) {
    params.set('apiIds', apiIds.join(','));
  }
  if (options.resultsWarehouseIds && options.resultsWarehouseIds.length > 0) {
    params.set('resultsWarehouseIds', options.resultsWarehouseIds.join(','));
  }
  if (options.relatedResultsModelId) {
    params.set('relatedResultsModelId', options.relatedResultsModelId);
  }
  params.set('browserTimezone', moment.tz.guess());
  return net.getJSONPromise(`/1/api/audits:get-filter-options?${params.toString()}`);
}

export function getAuditLogsData(
  apiIds: Array<string>,
  options: {
    columnNames?: Array<string>;
    resultsWarehouseIds?: Array<string>;
    relatedResultsModelId?: string;
    pageSize?: number;
    pageOffset?: number;
    userIds?: Array<string>;
    actions?: Array<ActionFilterCriteria>;
    reasonCodes?: Array<string>;
    startDate?: string;
    endDate?: string;
  } = {}
): Promise<AuditLogsDataResponse> {
  return net.postJSONPromise({
    data: {
      api_ids: apiIds,
      column_names: options.columnNames,
      results_warehouse_ids: options.resultsWarehouseIds,
      related_results_model_id: options.relatedResultsModelId,
      page_size: options.pageSize,
      page_offset: options.pageOffset,
      browser_timezone: moment.tz.guess(),
      user_ids: options.userIds,
      actions: options.actions,
      reason_codes: options.reasonCodes,
      start_date: options.startDate,
      end_date: options.endDate,
    },
    url: '/1/api/audits:get-paginated-data',
  });
}

export function exportAuthenticationOptionsReport(
  format: AuditLogFormat
): JQueryPromise<ExportAuditLogsResponse> {
  return TaskRunner.postJSON({
    data: {export_format: format, browser_timezone: moment.tz.guess()},
    url: '/1/api/audits:export-authentication-options-audit-logs',
  });
}
